import { TableToolbar, TableFilter, SortableHeader } from '../TableControls'
import { matchesTableSearch, sortTableRows, useTableSort } from '../../lib/tableControls'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { ArrowDown, ArrowRight, ArrowUp, FolderOpen, GitBranch, Pencil, Plus, Trash2 } from 'lucide-react'

import { daysAgoKey, toDateKey } from '../../lib/agentUsage'
import { formatEstimatedCost } from '../../lib/modelPricing'
import {
  PATTERN_MAX_LENGTH,
  createProjectRule,
  deleteProjectRule,
  fetchUnassignedUsage,
  friendlyRuleError,
  listProjectRules,
  moveRule,
  nextPriority,
  prefillFromUnassignedRow,
  reorderProjectRules,
  sumUnassigned,
  updateProjectRule,
  validatePattern,
  type ProjectRule,
  type RuleMatchType,
  type UnassignedUsageRow,
} from '../../lib/projectRules'
import { LIST_PRICE_LABEL } from '../UsageCost'
import { Card, CardContent } from '../ui/card'
import { Badge } from '../ui/badge'
import { Skeleton } from '../ui/skeleton'
import { Button, buttonVariants } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog'

// ============================================================================
// Projects page: "Attribution rules" + "Unassigned usage" (Phase 1.1 of
// docs/TOKEN_COST_ACCURACY_PLAN.md). Data access lives in
// src/lib/projectRules.ts.
//
// Role behaviour (mirrors RLS on project_rules + get_team_unassigned_usage):
//   admin   -- full CRUD + reorder, "Create rule" from unassigned rows;
//   manager -- read-only rules, unassigned usage for their pods' agents;
//   member  -- nothing rendered (the parent doesn't mount this at all).
// ============================================================================

const UNASSIGNED_WINDOW_DAYS = 30
const UNASSIGNED_ROW_LIMIT = 25

interface ProjectOption {
  id: string
  name: string
}

interface ProjectAttributionProps {
  teamId: string
  isAdmin: boolean
  projects: ProjectOption[]
  /** Called after any rule change so the page can refetch its project usage figures. */
  onRulesChanged?: () => void | Promise<void>
}

interface FormState {
  open: boolean
  editing: ProjectRule | null
  matchType: RuleMatchType
  pattern: string
  projectId: string
  suggestedPattern: string | null
}

const CLOSED_FORM: FormState = {
  open: false,
  editing: null,
  matchType: 'repo',
  pattern: '',
  projectId: '',
  suggestedPattern: null,
}

const tokenFormatter = new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 })

function formatLastSeen(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

const MatchTypeBadge: React.FC<{ type: RuleMatchType }> = ({ type }) => (
  <Badge variant="secondary" className="shrink-0 gap-1">
    {type === 'repo' ? <GitBranch className="h-3 w-3" /> : <FolderOpen className="h-3 w-3" />}
    {type === 'repo' ? 'Repo' : 'Folder'}
  </Badge>
)

export const ProjectAttribution: React.FC<ProjectAttributionProps> = ({
  teamId,
  isAdmin,
  projects,
  onRulesChanged,
}) => {
  const [rules, setRules] = useState<ProjectRule[]>([])
  const [rulesLoading, setRulesLoading] = useState(true)
  const [rulesError, setRulesError] = useState<string | null>(null)

  const [unassigned, setUnassigned] = useState<UnassignedUsageRow[]>([])
  const [usageSearch, setUsageSearch] = useState('')
  const [locationFilter, setLocationFilter] = useState('all')
  const { sort, toggleSort } = useTableSort('tokens', 'desc')
  const [unassignedLoading, setUnassignedLoading] = useState(true)
  const [unassignedError, setUnassignedError] = useState<string | null>(null)

  const [form, setForm] = useState<FormState>(CLOSED_FORM)
  const [formError, setFormError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const [deleteTarget, setDeleteTarget] = useState<ProjectRule | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [reordering, setReordering] = useState(false)

  const projectNameById = useMemo(() => new Map(projects.map((p) => [p.id, p.name])), [projects])

  const loadRules = useCallback(async () => {
    setRulesLoading(true)
    setRulesError(null)
    try {
      setRules(await listProjectRules(teamId))
    } catch (err) {
      console.error('Failed to load project rules:', err)
      setRulesError('Could not load attribution rules.')
    } finally {
      setRulesLoading(false)
    }
  }, [teamId])

  const loadUnassigned = useCallback(async () => {
    setUnassignedLoading(true)
    setUnassignedError(null)
    try {
      setUnassigned(
        await fetchUnassignedUsage(teamId, daysAgoKey(UNASSIGNED_WINDOW_DAYS - 1), toDateKey(new Date()))
      )
    } catch (err) {
      console.error('Failed to load unassigned usage:', err)
      setUnassignedError('Could not load unassigned usage.')
    } finally {
      setUnassignedLoading(false)
    }
  }, [teamId])

  useEffect(() => {
    loadRules()
    loadUnassigned()
  }, [loadRules, loadUnassigned])

  /** Rules decide attribution at read time, so every change moves usage: refetch all three. */
  const refreshAfterChange = useCallback(async () => {
    await Promise.all([loadRules(), loadUnassigned(), onRulesChanged?.()])
  }, [loadRules, loadUnassigned, onRulesChanged])

  const visibleUnassigned = sortTableRows(unassigned.filter(row => matchesTableSearch(usageSearch, row.gitRepo, row.cwdRel)
    && (locationFilter === 'all' || (locationFilter === 'repo' ? !!row.gitRepo : !row.gitRepo))), sort, {
      location: row => row.gitRepo || row.cwdRel, tokens: row => row.totalTokens, cost: row => row.costUsd,
      events: row => row.eventCount, seen: row => row.lastSeen ? Date.parse(row.lastSeen) : null,
    })

  const unassignedTotals = useMemo(() => sumUnassigned(unassigned), [unassigned])

  // ---- form -----------------------------------------------------------------

  const openCreate = (prefill?: Partial<FormState>) => {
    setFormError(null)
    setForm({ ...CLOSED_FORM, projectId: projects[0]?.id ?? '', ...prefill, open: true })
  }

  const openEdit = (rule: ProjectRule) => {
    setFormError(null)
    setForm({
      open: true,
      editing: rule,
      matchType: rule.matchType,
      pattern: rule.pattern,
      projectId: projectNameById.has(rule.projectId) ? rule.projectId : '',
      suggestedPattern: null,
    })
  }

  const openFromUnassigned = (row: UnassignedUsageRow) => {
    const prefill = prefillFromUnassignedRow(row)
    if (!prefill) return
    openCreate(prefill)
  }

  const patternError = form.open ? validatePattern(form.pattern) : null

  const handleSave = async () => {
    const invalid = validatePattern(form.pattern)
    if (invalid) {
      setFormError(invalid)
      return
    }
    if (!form.projectId) {
      setFormError('Choose a project.')
      return
    }
    setSaving(true)
    setFormError(null)
    try {
      const input = { matchType: form.matchType, pattern: form.pattern, projectId: form.projectId }
      if (form.editing) {
        await updateProjectRule(form.editing.id, input)
        toast.success('Rule updated')
      } else {
        await createProjectRule(teamId, input, nextPriority(rules))
        toast.success('Rule added')
      }
      setForm(CLOSED_FORM)
      await refreshAfterChange()
    } catch (err) {
      console.error('Failed to save project rule:', err)
      setFormError(friendlyRuleError(err))
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    setDeleting(true)
    try {
      await deleteProjectRule(deleteTarget.id)
      toast.success('Rule deleted')
      setDeleteTarget(null)
      await refreshAfterChange()
    } catch (err) {
      toast.error(friendlyRuleError(err))
    } finally {
      setDeleting(false)
    }
  }

  const handleMove = async (id: string, direction: 'up' | 'down') => {
    const reordered = moveRule(rules, id, direction)
    const previous = rules
    setRules(reordered)
    setReordering(true)
    try {
      await reorderProjectRules(reordered)
      await refreshAfterChange()
    } catch (err) {
      // Some of the parallel updates may have landed; reload rather than guess.
      setRules(previous)
      toast.error(friendlyRuleError(err))
      await loadRules()
    } finally {
      setReordering(false)
    }
  }

  // ---- render ---------------------------------------------------------------

  return (
    <>
      <div className="space-y-6">
      {/* id target for ProjectRulesNudge.tsx's deep link from the connect-agent
          flows -- #attribution-rules on /dashboard/team/:teamId/projects. */}
      <Card id="attribution-rules" className="scroll-mt-4 bg-background">
        <CardContent className="space-y-4 p-4 sm:p-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-foreground">Attribution rules</h2>
              <p className="text-sm text-muted-foreground">
                Decide which project each AI-connector usage event counts toward.
                {!isAdmin && ' Only admins can change these rules.'}
              </p>
            </div>
            {isAdmin && (
              <Button
                variant="outline"
                className="min-h-[44px] shrink-0 gap-2"
                disabled={projects.length === 0}
                onClick={() => openCreate()}
              >
                <Plus className="h-4 w-4" />
                Add rule
              </Button>
            )}
          </div>

          <div className="space-y-2 border border-border bg-muted/40 p-3 text-xs text-muted-foreground sm:p-4">
            <p className="font-medium text-foreground">How a usage event is assigned (first match wins)</p>
            <ol className="list-decimal space-y-1 pl-4">
              <li>
                A <code className="font-mono text-foreground">.syncsimple.json</code> file in the repo root or
                working folder, e.g.{' '}
                <code className="break-all font-mono text-foreground">{'{"project": "Marketing"}'}</code> (project
                name or id). It overrides the rules below for usage in that folder.
              </li>
              <li>The first matching rule below, top to bottom.</li>
              <li>The connector&apos;s default project, set on the connector&apos;s own page.</li>
              <li>Otherwise it&apos;s Unassigned.</li>
            </ol>
            <p>
              Rule changes apply to past usage too — figures are recalculated whenever they&apos;re read.
            </p>
          </div>

          {rulesError && <p className="text-sm text-destructive">{rulesError}</p>}

          {!rulesError && rulesLoading && rules.length === 0 && (
            // Mirrors a loaded rule row's shape (index, badge, pattern) as a
            // few repeated placeholder rows.
            <ol className="-mx-4 divide-y divide-border border-y border-border sm:-mx-6">
              {[0, 1, 2].map((i) => (
                <li key={i} className="flex min-h-[44px] items-center gap-2 px-4 py-2 sm:px-6">
                  <Skeleton className="h-6 w-16 shrink-0 rounded-full" />
                  <Skeleton className="h-4 w-40" />
                </li>
              ))}
            </ol>
          )}

          {!rulesError && !rulesLoading && rules.length === 0 && (
            <p className="text-sm text-muted-foreground">
              {isAdmin
                ? projects.length === 0
                  ? 'Create a project first, then add a rule to route usage to it.'
                  : 'No rules yet. Add one, or use "Create rule" on an unassigned repo below.'
                : 'No rules yet.'}
            </p>
          )}

          {rules.length > 0 && (
            <ol className="-mx-4 divide-y divide-border border-y border-border sm:-mx-6">
              {rules.map((rule, i) => {
                const projectName = projectNameById.get(rule.projectId)
                return (
                  <li
                    key={rule.id}
                    className="flex flex-col gap-2 px-4 py-2 sm:flex-row sm:items-center sm:gap-3 sm:px-6"
                  >
                    <div className="flex min-h-[44px] min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1">
                      <span className="w-6 shrink-0 text-xs tabular-nums text-muted-foreground">{i + 1}.</span>
                      <MatchTypeBadge type={rule.matchType} />
                      <code className="min-w-0 break-all font-mono text-sm text-foreground">{rule.pattern}</code>
                      <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-label="assigns to" />
                      {projectName ? (
                        <span className="text-sm font-medium text-foreground">{projectName}</span>
                      ) : (
                        <span className="text-sm italic text-muted-foreground">Deleted project (rule ignored)</span>
                      )}
                    </div>
                    {isAdmin && (
                      <div className="flex shrink-0 items-center gap-1 self-end sm:self-auto">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-11 w-11"
                          aria-label="Move rule up"
                          disabled={i === 0 || reordering}
                          onClick={() => handleMove(rule.id, 'up')}
                        >
                          <ArrowUp className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-11 w-11"
                          aria-label="Move rule down"
                          disabled={i === rules.length - 1 || reordering}
                          onClick={() => handleMove(rule.id, 'down')}
                        >
                          <ArrowDown className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-11 w-11"
                          aria-label="Edit rule"
                          onClick={() => openEdit(rule)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-11 w-11 text-destructive hover:text-destructive"
                          aria-label="Delete rule"
                          onClick={() => setDeleteTarget(rule)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </li>
                )
              })}
            </ol>
          )}
        </CardContent>
      </Card>

      <Card className="bg-background">
        <CardContent className="space-y-4 p-4 sm:p-6">
          <div>
            <h2 className="text-base font-semibold text-foreground">Unassigned usage</h2>
            <p className="text-sm text-muted-foreground">
              Last {UNASSIGNED_WINDOW_DAYS} days of usage that no hint, rule or connector default assigns to a project
              {isAdmin ? '.' : ', for connectors in the teams you manage.'}
            </p>
          </div>

          {unassignedError && <p className="text-sm text-destructive">{unassignedError}</p>}

          {!unassignedError && (
            <div className="grid grid-cols-1 divide-y divide-border border border-border sm:grid-cols-3 sm:divide-x sm:divide-y-0">
              <div className="p-4">
                <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Tokens</div>
                <div className="text-2xl font-semibold tabular-nums text-foreground">
                  {unassignedLoading ? <Skeleton className="h-7 w-16" /> : unassignedTotals.totalTokens.toLocaleString()}
                </div>
              </div>
              <div className="p-4">
                <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Cost</div>
                <div className="text-2xl font-semibold tabular-nums text-foreground">
                  {unassignedLoading ? (
                    <Skeleton className="h-7 w-16" />
                  ) : unassignedTotals.costUsd === null ? (
                    unassignedTotals.eventCount > 0 ? (
                      'Unpriced'
                    ) : (
                      '—'
                    )
                  ) : (
                    `~${formatEstimatedCost(unassignedTotals.costUsd)}`
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  {unassignedLoading ? <Skeleton className="h-3 w-20" /> : LIST_PRICE_LABEL}
                </p>
              </div>
              <div className="p-4">
                <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Events</div>
                <div className="text-2xl font-semibold tabular-nums text-foreground">
                  {unassignedLoading ? <Skeleton className="h-7 w-12" /> : unassignedTotals.eventCount.toLocaleString()}
                </div>
              </div>
            </div>
          )}

          {!unassignedError && unassignedLoading && unassigned.length === 0 && (
            // Mirrors a loaded table row: location icon + path, three
            // right-aligned numeric columns.
            <div className="-mx-4 divide-y divide-border border-y border-border sm:-mx-6">
              {[0, 1, 2].map((i) => (
                <div key={i} className="flex items-center gap-4 px-4 py-3 sm:px-6">
                  <Skeleton className="h-4 w-4 shrink-0 rounded-md" />
                  <Skeleton className="h-4 min-w-0 flex-1" />
                  <Skeleton className="h-4 w-12 shrink-0" />
                  <Skeleton className="h-4 w-12 shrink-0" />
                  <Skeleton className="h-4 w-16 shrink-0" />
                </div>
              ))}
            </div>
          )}

          {!unassignedError && !unassignedLoading && unassigned.length === 0 && (
            <p className="text-sm text-muted-foreground">Nothing unassigned — every event has a project.</p>
          )}

          {!unassignedError && !unassignedLoading && <TableToolbar label="unassigned usage" query={usageSearch} onQueryChange={setUsageSearch} count={visibleUnassigned.length} total={unassigned.length}
            active={!!usageSearch || locationFilter !== 'all'} onClear={() => { setUsageSearch(''); setLocationFilter('all') }}>
            <TableFilter label="Filter location type" value={locationFilter} onChange={setLocationFilter} allLabel="All locations" options={[{ value: 'repo', label: 'Repositories' }, { value: 'folder', label: 'Folders / unknown' }]} />
          </TableToolbar>}
          {!unassignedError && !unassignedLoading && unassigned.length > 0 && visibleUnassigned.length === 0 && <p className="text-sm text-muted-foreground">No usage matches these filters.</p>}
          {!unassignedError && visibleUnassigned.length > 0 && (
            <div className="-mx-4 sm:-mx-6">
              {/* Table's own wrapper is overflow-auto, so wide rows scroll inside it, never the page. */}
              <Table className="min-w-[640px]">
                <TableHeader>
                  <TableRow>
                    <SortableHeader className="pl-4 sm:pl-6" column="location" label="Repo / folder" sort={sort} onSort={toggleSort} />
                    <SortableHeader className="text-right" column="tokens" label="Tokens" sort={sort} onSort={toggleSort} />
                    <SortableHeader className="text-right" column="cost" label="Cost" sort={sort} onSort={toggleSort} />
                    <SortableHeader className="text-right" column="events" label="Events" sort={sort} onSort={toggleSort} />
                    <SortableHeader className="text-right" column="seen" label="Last seen" sort={sort} onSort={toggleSort} />
                    {isAdmin && (
                      <TableHead className="pr-4 text-right sm:pr-6">
                        <span className="sr-only">Actions</span>
                      </TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody className="border-b border-border [&_tr:last-child]:border-b">
                  {visibleUnassigned.slice(0, UNASSIGNED_ROW_LIMIT).map((row) => {
                    const prefill = prefillFromUnassignedRow(row)
                    return (
                      <TableRow key={`${row.gitRepo ?? ''}\u0000${row.cwdRel ?? ''}`}>
                        <TableCell className="pl-4 sm:pl-6">
                          <div className="flex min-w-0 items-center gap-2">
                            {row.gitRepo ? (
                              <GitBranch className="h-4 w-4 shrink-0 text-muted-foreground" />
                            ) : (
                              <FolderOpen className="h-4 w-4 shrink-0 text-muted-foreground" />
                            )}
                            <div className="min-w-0">
                              <div className="break-all font-mono text-sm text-foreground">
                                {row.gitRepo ?? row.cwdRel ?? 'Unknown location'}
                              </div>
                              {row.gitRepo && row.cwdRel && (
                                <div className="break-all font-mono text-xs text-muted-foreground">{row.cwdRel}</div>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {tokenFormatter.format(row.totalTokens)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {row.costUsd === null ? (
                            <span className="text-muted-foreground">Unpriced</span>
                          ) : (
                            `~${formatEstimatedCost(row.costUsd)}`
                          )}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{row.eventCount.toLocaleString()}</TableCell>
                        <TableCell className="whitespace-nowrap text-right text-muted-foreground">
                          {formatLastSeen(row.lastSeen)}
                        </TableCell>
                        {isAdmin && (
                          <TableCell className="pr-4 text-right sm:pr-6">
                            <Button
                              variant="outline"
                              size="sm"
                              className="min-h-[44px] whitespace-nowrap"
                              disabled={!prefill || projects.length === 0}
                              onClick={() => openFromUnassigned(row)}
                            >
                              Create rule
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
              {visibleUnassigned.length > UNASSIGNED_ROW_LIMIT && (
                <p className="px-4 pt-2 text-xs text-muted-foreground sm:px-6">
                  Showing the first {UNASSIGNED_ROW_LIMIT} of {visibleUnassigned.length.toLocaleString()} matching locations.
                  Totals above include all of them.
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
      </div>

      <Dialog open={form.open} onOpenChange={(open) => !open && !saving && setForm(CLOSED_FORM)}>
        <DialogContent className="max-h-[90vh] w-[calc(100%-2rem)] overflow-y-auto sm:w-full">
          <DialogHeader>
            <DialogTitle>{form.editing ? 'Edit rule' : 'Add rule'}</DialogTitle>
            <DialogDescription>
              Usage whose {form.matchType === 'repo' ? 'git repository' : 'working folder'} matches this pattern
              counts toward the chosen project.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="rule-match-type">Match on</Label>
              <Select
                value={form.matchType}
                onValueChange={(v) => setForm((f) => ({ ...f, matchType: v as RuleMatchType }))}
              >
                <SelectTrigger id="rule-match-type" className="min-h-[44px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="repo">Repository (e.g. acme/website)</SelectItem>
                  <SelectItem value="folder">Folder (e.g. packages/web or ~/finance/**)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="rule-pattern">Pattern</Label>
              <Input
                id="rule-pattern"
                className="min-h-[44px] font-mono"
                maxLength={PATTERN_MAX_LENGTH + 50}
                placeholder={form.matchType === 'repo' ? 'acme/*' : '~/finance/**'}
                value={form.pattern}
                onChange={(e) => setForm((f) => ({ ...f, pattern: e.target.value }))}
                aria-invalid={!!formError && !!patternError}
              />
              {form.suggestedPattern && form.pattern !== form.suggestedPattern && (
                <Button
                  type="button"
                  variant="link"
                  className="h-auto min-h-[44px] px-0 text-xs"
                  onClick={() => setForm((f) => ({ ...f, pattern: f.suggestedPattern ?? f.pattern }))}
                >
                  Use <code className="mx-1 font-mono">{form.suggestedPattern}</code> to include subfolders
                </Button>
              )}
              <div className="space-y-1 text-xs text-muted-foreground">
                <p>
                  Case-insensitive. <code className="font-mono">*</code> matches anything except{' '}
                  <code className="font-mono">/</code>, <code className="font-mono">**</code> matches anything
                  including <code className="font-mono">/</code>, <code className="font-mono">?</code> matches one
                  character.
                </p>
                {form.matchType === 'repo' ? (
                  <p>
                    Matches the repo slug, e.g. <code className="font-mono">acme/website</code> or{' '}
                    <code className="font-mono">acme/*</code>.
                  </p>
                ) : (
                  <p>
                    Inside a repo, matches the folder relative to the repo root (e.g.{' '}
                    <code className="font-mono">packages/web</code>); outside a repo, the{' '}
                    <code className="font-mono">~</code>-shortened path (e.g.{' '}
                    <code className="font-mono">~/finance/**</code>). A trailing{' '}
                    <code className="font-mono">/**</code> also matches the folder itself.
                  </p>
                )}
                <p className="tabular-nums">
                  {form.pattern.trim().length}/{PATTERN_MAX_LENGTH}
                </p>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="rule-project">Project</Label>
              <Select value={form.projectId} onValueChange={(v) => setForm((f) => ({ ...f, projectId: v }))}>
                <SelectTrigger id="rule-project" className="min-h-[44px]">
                  <SelectValue placeholder="Choose a project" />
                </SelectTrigger>
                <SelectContent>
                  {projects.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {formError && (
              <p role="alert" className="text-sm text-destructive">
                {formError}
              </p>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" className="min-h-[44px]" disabled={saving} onClick={() => setForm(CLOSED_FORM)}>
              Cancel
            </Button>
            <Button className="min-h-[44px]" disabled={saving || !!patternError || !form.projectId} onClick={handleSave}>
              {saving ? 'Saving…' : form.editing ? 'Save changes' : 'Add rule'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && !deleting && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this rule?</AlertDialogTitle>
            <AlertDialogDescription>
              Usage matching <code className="break-all font-mono">{deleteTarget?.pattern}</code> will fall through
              to the next matching rule, the connector&apos;s default project, or Unassigned — including past usage.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className={buttonVariants({ variant: 'destructive' })}
              disabled={deleting}
              onClick={(e) => {
                e.preventDefault()
                handleDelete()
              }}
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

export default ProjectAttribution
